import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ChevronDown, ChevronRight, Search, Archive, RotateCcw } from "lucide-react";
import LifecycleStatus from "@/components/admin/LifecycleStatus";
import ClubPointsButton from "@/components/admin/ClubPointsButton";
import EmailDetective from "@/components/admin/EmailDetective";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (s) => (s ? new Date(s).toLocaleDateString() : "—");
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const bal = (b) => Number(b.total_price || 0) - Number(b.discount || 0) - Number(b.amount_paid || 0);
// Rental status (lifecycle_status) is independent of payment status — an outstanding
// balance must never keep a Returned/Closed rental stuck in Active Bookings.
export const isCompletedRental = (b) =>
  b.status !== "cancelled" && ["closed", "returned"].includes(b.lifecycle_status);

function ArchiveDetail({ id, token }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/bookings/${id}/archive`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [id, token]);
  if (!d) return <div className="py-3 text-white/40 text-xs flex gap-2 items-center"><Loader2 size={12} className="animate-spin" /> Loading history…</div>;
  if (d.error) return <div className="py-3 text-red-300 text-xs">Could not load history.</div>;
  const b = d.booking;
  const Section = ({ title, children }) => (
    <div className="bg-black/40 border border-white/10 rounded-md p-3 min-w-0">
      <div className="text-white/40 text-[10px] uppercase tracking-wider mb-1.5">{title}</div>{children}
    </div>
  );
  return (
    <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5 text-xs" data-testid={`archive-detail-${id}`}>
      <Section title="Payment History">
        <div className="flex justify-between"><span className="text-white/50">Total</span><span>{usd(b.total_price)}</span></div>
        {Number(b.discount) > 0 && <div className="flex justify-between"><span className="text-white/50">Discount</span><span>−{usd(b.discount)}</span></div>}
        <div className="flex justify-between"><span className="text-white/50">Paid</span><span className="text-green-400">{usd(b.amount_paid)}</span></div>
        <div className="flex justify-between border-t border-white/10 mt-1 pt-1"><span className="text-white/50">Balance</span>
          <span className={bal(b) > 0.01 ? "text-red-300" : "text-green-400"}>{usd(bal(b))}</span></div>
        {b.payment_method && <div className="text-white/35 mt-1">Method: {b.payment_method}</div>}
      </Section>
      <Section title={`Agreements (${d.agreements.length})`}>
        {d.agreements.length === 0 && <span className="text-white/35">None on file</span>}
        {d.agreements.map((a) => (
          <div key={a.id} className="flex justify-between"><span className="text-white/60">{a.status || "agreement"}</span>
            <span className="text-white/40">{a.signed_at ? `signed ${fmt(a.signed_at)}` : fmt(a.created_at)}</span></div>
        ))}
      </Section>
      <Section title={`Extensions (${d.extensions.length})`}>
        {d.extensions.length === 0 && <span className="text-white/35">None</span>}
        {d.extensions.map((e) => (
          <div key={e.id} className="flex justify-between"><span className="text-white/60">#{e.number} → {e.new_return_date}</span>
            <span className="text-white/40">{usd(e.total)}</span></div>
        ))}
      </Section>
      <Section title={`Inspections (${d.inspections.length})`}>
        {d.inspections.length === 0 && <span className="text-white/35">None recorded</span>}
        {d.inspections.map((i) => (
          <div key={i.id} className="flex justify-between"><span className="text-white/60">{i.type || "inspection"} ({i.photos} photos)</span>
            <span className="text-white/40">{fmt(i.created_at)}</span></div>
        ))}
      </Section>
      <Section title={`Damage Charges (${d.damage_charges.length})`}>
        {d.damage_charges.length === 0 && <span className="text-white/35">None — clean return</span>}
        {d.damage_charges.map((c) => (
          <div key={c.id} className="flex justify-between"><span className="text-white/60">{c.description || "charge"}</span>
            <span className="text-red-300">{usd(c.amount)}</span></div>
        ))}
      </Section>
      <Section title="Notes & Communication">
        {b.message && <div className="text-white/55 mb-1">"{b.message}"</div>}
        {d.reservation_request ? (
          <div className="space-y-0.5 max-h-32 overflow-y-auto">
            {(d.reservation_request.comm_log || []).slice(-6).map((c, i) => (
              <div key={i} className="text-white/45">• [{c.kind}] {c.detail?.slice(0, 80)}</div>
            ))}
          </div>
        ) : <span className="text-white/35">No linked reservation request</span>}
      </Section>
    </div>
  );
}

export default function CompletedRentals({ bookings, token, onRefresh }) {
  const [q, setQ] = useState("");
  const [range, setRange] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [open, setOpen] = useState(null);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const cut = (days) => { const d = new Date(today); d.setDate(d.getDate() - days); return d; };
  const inRange = (b) => {
    const d = new Date((b.return_date || b.created_at || "").slice(0, 10));
    if (range === "today") return d >= today;
    if (range === "week") return d >= cut(7);
    if (range === "month") return d >= cut(31);
    if (range === "custom") return (!from || d >= new Date(from)) && (!to || d <= new Date(to));
    return true;
  };
  const ql = q.toLowerCase();
  const rows = bookings.filter(inRange).filter((b) =>
    !ql || [b.name, b.email, b.phone, b.vehicle].some((x) => (x || "").toLowerCase().includes(ql)))
    .sort((a, b) => (b.return_date || "").localeCompare(a.return_date || ""));

  return (
    <div className="space-y-3" data-testid="completed-rentals">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display font-black text-2xl flex items-center gap-2"><Archive size={20} className="text-[#F2B705]" /> Completed Rentals</h2>
        <span className="text-white/40 text-sm">({rows.length})</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-2.5 text-white/30" />
          <input data-testid="completed-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, vehicle, email…"
            className="bg-[#0C0C0C] border border-white/15 rounded-md pl-8 pr-3 py-1.5 text-sm text-white w-64" />
        </div>
        <div className="inline-flex rounded-lg border border-white/10 bg-[#0A0A0A] p-0.5">
          {[["all", "All"], ["today", "Today"], ["week", "This Week"], ["month", "This Month"], ["custom", "Custom"]].map(([k, l]) => (
            <button key={k} data-testid={`completed-filter-${k}`} onClick={() => setRange(k)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-md ${range === k ? "bg-[#F2B705] text-black" : "text-white/55 hover:text-white"}`}>{l}</button>
          ))}
        </div>
        {range === "custom" && (
          <div className="flex items-center gap-1.5 text-xs text-white/50">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-[#0C0C0C] border border-white/15 rounded px-2 py-1 text-white" data-testid="completed-from" />
            →
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bg-[#0C0C0C] border border-white/15 rounded px-2 py-1 text-white" data-testid="completed-to" />
          </div>
        )}
      </div>
      {rows.length === 0 && <p className="text-white/40 text-sm">No completed rentals {q || range !== "all" ? "match your filters" : "yet — rentals move here automatically once marked Returned"}.</p>}
      {rows.map((b) => (
        <div key={b.id} data-testid={`completed-row-${b.id}`} className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4">
          <button className="w-full flex flex-wrap items-center gap-3 text-left" onClick={() => setOpen(open === b.id ? null : b.id)} data-testid={`completed-expand-${b.id}`}>
            {open === b.id ? <ChevronDown size={15} className="text-[#F2B705]" /> : <ChevronRight size={15} className="text-white/40" />}
            <span className="font-display font-bold">{b.name}</span>
            <span className="text-[#F2B705] text-sm font-semibold">{b.vehicle}</span>
            <span className="text-white/45 text-xs">{b.pickup_date} → {b.return_date}</span>
            {bal(b) > 0.01 && (
              <span data-testid={`completed-balance-due-${b.id}`} className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 text-[10px] font-bold">
                Balance Due {usd(bal(b))}
              </span>
            )}
            <span className="ml-auto text-green-400 text-sm font-bold">{usd(b.amount_paid)}</span>
            <span className="px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 text-[10px] font-semibold">✓ {b.lifecycle_status === "closed" ? "Closed" : "Returned"}</span>
          </button>
          {open === b.id && (
            <>
              <StatusOverrideBar booking={b} token={token} onRefresh={onRefresh} />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <ClubPointsButton booking={b} token={token} onSaved={onRefresh} />
                {b.club_points_msg_sent_at && <span className="text-white/35 text-[11px]">Points message sent {fmtDateTime(b.club_points_msg_sent_at)}</span>}
                <EmailDetective booking={b} token={token} />
              </div>
              <ArchiveDetail id={b.id} token={token} />
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function StatusOverrideBar({ booking, token, onRefresh }) {
  const [busy, setBusy] = useState(false);

  const reopen = async () => {
    if (!window.confirm(`Put ${booking.name}'s ${booking.vehicle} back OUT as an active rental? It will move back to Active Bookings.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/bookings/${booking.id}/status`, { lifecycle_status: "active" },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${booking.name} is back to Rented (Active) — check the Active Bookings tab.`);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 bg-[#F2B705]/[0.06] border border-[#F2B705]/25 rounded-md p-3 flex flex-wrap items-center gap-3"
      data-testid={`status-override-${booking.id}`}>
      <button data-testid={`reopen-rental-${booking.id}`} onClick={reopen} disabled={busy}
        className="flex items-center gap-2 bg-[#F2B705] text-black text-xs font-bold px-4 py-2 rounded-md disabled:opacity-50">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Put Back Out — Mark as Rented
      </button>
      <div className="flex items-center gap-2">
        <span className="text-white/40 text-[11px] uppercase tracking-wider">Or set status manually:</span>
        <LifecycleStatus booking={booking} token={token} onSaved={onRefresh} />
      </div>
    </div>
  );
}

export function DeclinedRequests({ token }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    axios.get(`${API}/reservations?status=Declined`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [token]);
  if (!rows) return <div className="text-white/40 text-xs flex gap-2 items-center py-2"><Loader2 size={12} className="animate-spin" /> Loading declined requests…</div>;
  return (
    <div className="space-y-2 mt-6" data-testid="declined-requests">
      <h3 className="font-display font-bold text-lg text-white/80">Declined Reservation Requests ({rows.length})</h3>
      {rows.length === 0 && <p className="text-white/40 text-sm">No declined requests.</p>}
      {rows.map((r) => (
        <div key={r.id} className="bg-[#0C0C0C] border border-white/10 rounded-lg px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`declined-row-${r.id}`}>
          <span className="font-semibold text-sm">{r.name}</span>
          <span className="text-white/45 text-xs">{r.pickup_date} → {r.return_date}</span>
          <span className="text-white/35 text-xs">{r.vehicle || "any vehicle"}</span>
          {r.declined_by === "AI Assistant" && <span className="px-2 py-0.5 rounded-full bg-red-500/10 text-red-300 text-[10px]">AI auto-decline</span>}
          <span className="ml-auto text-white/40 text-xs truncate max-w-md" title={r.decline_reason_internal}>{r.decline_reason_internal || "—"}</span>
        </div>
      ))}
    </div>
  );
}
